export type Language = "en" | "fr" | "pt";
export type DispatchChannel = "email" | "pdf_download" | "both";
export type PassType = "annual" | "session" | "day";
export type PassStatus = "active" | "expired" | "revoked";
export type RequestStatus =
  | "pending"
  | "docs_requested"
  | "under_review"
  | "approved"
  | "rejected";
export type AccreditationTerm = "short_term" | "medium_term" | "long_term";

export const TERM_LABELS: Record<AccreditationTerm, string> = {
  short_term: "Short term",
  medium_term: "Medium term",
  long_term: "Long term",
};

export const TERM_DESCRIPTIONS: Record<AccreditationTerm, string> = {
  short_term: "A specific event or programme — no longer than 1 month",
  medium_term: "Reviewed every 6 months",
  long_term: "Standing press corps membership — reviewed every 2 years",
};

export const TERM_BADGE_CLASSES: Record<AccreditationTerm, string> = {
  short_term: "bg-blue-100 text-blue-800 border-blue-300",
  medium_term: "bg-amber-100 text-amber-800 border-amber-300",
  long_term: "bg-emerald-100 text-emerald-800 border-emerald-300",
};

export function defaultValidToForTerm(term: AccreditationTerm, validFrom: string): string {
  const from = new Date(validFrom);
  if (term === "short_term") from.setDate(from.getDate() + 30);
  else if (term === "medium_term") from.setMonth(from.getMonth() + 6);
  else from.setFullYear(from.getFullYear() + 2);
  return from.toISOString().split("T")[0];
}

export interface JournalistOption {
  id: string;
  org_id: string;
  first_name: string;
  last_name: string;
  email: string | null;
  gender: string;
  nationality: string | null;
  outlet_name: string | null;
  outlet_id: string | null;
}

export interface InvitationLetter {
  id: string;
  org_id: string;
  journalist_id: string;
  language: Language;
  reference_number: string;
  pdf_url: string | null;
  dispatched_at: string | null;
  dispatch_channel: DispatchChannel;
  signed_by_name: string;
  signed_by_title: string;
  created_at: string;
  updated_at: string;
  journalist?: {
    first_name: string;
    last_name: string;
    email: string | null;
  };
  outlet_name?: string | null;
}

export interface AccreditationRequest {
  id: string;
  org_id: string;
  journalist_id: string;
  status: RequestStatus;
  accreditation_term: AccreditationTerm;
  document_flags: {
    national_id?: boolean;
    press_card?: boolean;
    assignment_letter?: boolean;
    photograph?: boolean;
  };
  document_urls?: {
    national_id?: string;
    press_card?: string;
    assignment_letter?: string;
    photograph?: string;
  };
  reviewer_notes: string | null;
  reviewed_by: string | null;
  reviewed_at: string | null;
  rejection_reason: string | null;
  created_at: string;
  updated_at: string;
  journalist?: {
    first_name: string;
    last_name: string;
    email: string | null;
  };
  outlet_name?: string | null;
}

export interface PressPass {
  id: string;
  org_id: string;
  journalist_id: string;
  pass_number: string;
  pass_type: PassType;
  accreditation_term: AccreditationTerm;
  valid_from: string;
  valid_to: string;
  status: PassStatus;
  issued_by: string | null;
  issued_at: string;
  revoked_by: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  qr_payload: string | null;
  created_at: string;
  updated_at: string;
  journalist?: {
    first_name: string;
    last_name: string;
    nationality: string | null;
    photo_url?: string | null;
    verification_id?: string | null;
    position?: string | null;
  };
  outlet_name?: string | null;
  organization?: {
    name: string;
    logo_url: string | null;
    brand_primary: string | null;
    brand_accent: string | null;
  } | null;
}

export const LETTER_TITLES: Record<Language, string> = {
  fr: "LETTRE D'INVITATION",
  en: "INVITATION LETTER",
  pt: "CARTA DE CONVITE",
};

export const SALUTATIONS: Record<Language, Record<string, string>> = {
  fr: { female: "Chère Madame", male: "Cher Monsieur", other: "", undisclosed: "" },
  en: { female: "Dear Madam", male: "Dear Sir", other: "", undisclosed: "" },
  pt: { female: "Cara Senhora", male: "Caro Senhor", other: "", undisclosed: "" },
};

export const CLOSING_FORMULAS: Record<Language, string> = {
  fr: "Je vous prie de croire, {salutation}, en ma parfaite considération.",
  en: "Yours sincerely,",
  pt: "Atenciosamente,",
};

export const PARAGRAPH_2: Record<Language, string> = {
  fr: "Votre mission inclut une large dissémination des informations importantes, sous divers formats, auprès de la presse et auprès de votre réseau de contacts dans la région et au-delà, en vous assurant que ces informations atteindront le grand public.",
  en: "Your assignment includes wide dissemination of important information in various formats to the press and your network of contacts in the region and beyond, ensuring that this information reaches the general public.",
  pt: "A sua missão inclui uma ampla divulgação de informações importantes, em diversos formatos, junto à imprensa e à sua rede de contatos na região e além, garantindo que essas informações cheguem ao grande público.",
};

export const PARAGRAPH_3: Record<Language, string> = {
  fr: "Toutes les informations de base qui devraient faciliter la bonne exécution de votre mission vous seront fournies par le Parlement de la CEDEAO à travers la Cheffe de la Division Communication.",
  en: "All background information necessary to facilitate your assignment will be provided by the ECOWAS Parliament through the Head of the Communication Division.",
  pt: "Todas as informações básicas que facilitem a boa execução da sua missão ser-lhe-ão fornecidas pelo Parlamento da CEDEAO através da Chefe da Divisão de Comunicação.",
};

export const PARAGRAPH_5: Record<Language, string> = {
  fr: "À des fins de vérification, vous êtes tenus de vous présenter au Parlement avec votre carte de presse. Celle-ci vous servira d'identification lors du paiement de vos indemnités.",
  en: "For verification purposes, you are required to present yourself at the Parliament with your press card. This will serve as identification for the payment of your allowances.",
  pt: "Para efeitos de verificação, é necessário que se apresente no Parlamento com o seu cartão de imprensa. Este servirá de identificação para o pagamento das suas diárias.",
};

export const ATTENDANCE_CODE_LABELS: Record<Language, string> = {
  fr: "Code d'accréditation :",
  en: "Accreditation code:",
  pt: "Código de acreditação:",
};

export const ATTENDANCE_TYPE_LABELS: Record<Language, Record<string, string>> = {
  fr: { physical: "Présence physique", online: "Participation en ligne" },
  en: { physical: "Physical attendance", online: "Online participation" },
  pt: { physical: "Presença física", online: "Participação online" },
};
